// UX-Vorschlag P3 in „Mein Tag": der gemeinsame Detailbereich fuer die
// Eintraege des Tagesplans und fuer Aufgaben aus „Braucht dich"/„Diese Woche",
// die (noch) nicht im Plan stehen — die kommen aus denselben Listen wie dort
// (GET /api/me/tasks bzw. /api/personal-tasks). Projekt-Status und Projektnamen kommen aus
// GET /api/me/tasks (wie in „Meine Aufgaben"); Aenderungen laufen ueber
// denselben Weg (lib/hubContext.ts) und laden danach den Tag neu.

import { useEffect, useMemo, useState } from "preact/hooks";
import type { PersonalTask, Task } from "@juuplan/shared";
import { TaskDetailPanel } from "../taskDetail/TaskDetailPanel";
import { api } from "../../lib/api";
import { applyHubChange, buildProjectMeta, type MeTask } from "../../lib/hubContext";
import { neighbourUid } from "../../lib/taskDetailModel";
import {
  personalTaskToRecord,
  projectTaskToRecordWithProject,
  type ResolverContext,
  type TaskRecord,
} from "../../lib/taskRecord";
import { toastError } from "../../lib/toast";
import { findUser } from "../../store/users";
import { refreshMyDay, type MyDayItem } from "../../store/myDay";

/** Records in Tagesplan-Reihenfolge (Eintraege ohne Aufgabe fallen weg). */
export function myDayRecords(items: MyDayItem[]): TaskRecord[] {
  const out: TaskRecord[] = [];
  for (const i of items) {
    if (i.kind === "personal" && i.personal_task) out.push(personalTaskToRecord(i.personal_task));
    else if (i.kind === "task" && i.task && i.task.project_id !== null)
      out.push(projectTaskToRecordWithProject(i.task as Task, i.task.project_id));
  }
  return out;
}

export function TodayDetail(props: {
  items: MyDayItem[];
  uid: string;
  onSelect: (uid: string | null) => void;
  onOpenFull: (record: TaskRecord) => void;
  /** Aufgabe weder im Plan noch in den Listen (z. B. kein Zugriff mehr). */
  onMissing: (uid: string) => void;
}): preact.JSX.Element | null {
  const { items, uid, onSelect, onOpenFull, onMissing } = props;
  const [meTasks, setMeTasks] = useState<MeTask[]>([]);
  const [personalTasks, setPersonalTasks] = useState<PersonalTask[]>([]);
  // Erst nach beiden Antworten darf „nicht gefunden" gelten.
  const [loaded, setLoaded] = useState(false);

  // meTasks: Status-Auswahl und Projektnamen; ohne sie bleibt „Offen/Erledigt".
  // Beide Listen liefern ausserdem die Aufgaben ausserhalb des Plans.
  const loadSources = async (): Promise<void> => {
    const [me, personal] = await Promise.all([
      api<MeTask[]>("/api/me/tasks").catch(() => [] as MeTask[]),
      api<PersonalTask[]>("/api/personal-tasks").catch(() => [] as PersonalTask[]),
    ]);
    setMeTasks(me);
    setPersonalTasks(personal);
    setLoaded(true);
  };

  useEffect(() => {
    void loadSources();
  }, []);

  const meta = useMemo(() => buildProjectMeta(meTasks), [meTasks]);
  const records = useMemo(() => myDayRecords(items), [items]);
  const record = useMemo(() => {
    const inPlan = records.find((r) => r.uid === uid);
    if (inPlan) return inPlan;
    const [source, rawId] = uid.split(":");
    const id = Number(rawId);
    if (source === "project") {
      const t = meTasks.find((m) => m.id === id);
      return t ? projectTaskToRecordWithProject(t, t.project_id) : null;
    }
    const pt = personalTasks.find((p) => p.id === id);
    return pt ? personalTaskToRecord(pt) : null;
  }, [records, uid, meTasks, personalTasks]);

  useEffect(() => {
    if (loaded && !record) onMissing(uid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded, record, uid]);

  const refresh = async (): Promise<void> => {
    await Promise.all([refreshMyDay(), loadSources()]);
  };

  const ctx: ResolverContext = useMemo(
    () => ({
      resolveProject: (id) => meta.projects.get(id),
      resolvePhase: () => undefined,
      resolveLabel: () => undefined,
      resolveUserName: (id) => findUser(id)?.name ?? `User #${id}`,
      resolveStatuses: (r) =>
        r.source === "project" && r.projectId !== null ? meta.statusSet.get(r.projectId) ?? [] : [],
      patch: async (r, change) => {
        try {
          const ok = await applyHubChange(r, change, meta);
          if (!ok) toastError("Kein passender Status im Projekt — bitte in der Aufgabe ändern.");
          await refresh();
        } catch {
          toastError("Änderung fehlgeschlagen");
        }
      },
    }),
    [meta]
  );

  if (!record) return null;
  return (
    <TaskDetailPanel
      record={record}
      ctx={ctx}
      onClose={() => onSelect(null)}
      onNavigate={(dir) => onSelect(neighbourUid(records.map((r) => r.uid), uid, dir))}
      onOpenFull={onOpenFull}
      onChanged={() => void refresh()}
    />
  );
}
